import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import currency from "currency.js";

import MoneyInput from "../../generic/MoneyInput";

// Plan de pagos MANUAL de los contratos de personas jurídicas: lista de hitos
// editable (concepto + valor), sin autogeneración desde honorarios ni cálculos
// de seguridad social/estampillas (eso es del flujo natural, PaymentPlanModal).
// Se persiste como paymentPlan: number[] + paymentPlanLabels: string[] para
// conservar el contrato de esquema con getContractField y el % de ejecución
// financiera (C8). El plan NO es obligatorio para aprobar el contrato (R6).

const LegalPaymentPlanModal = ({
  open,
  onClose,
  canEdit,
  contractBudget,
  paymentPlan,
  paymentPlanLabels,
  onSave,
}) => {
  const [values, setValues] = useState(null);
  const [labels, setLabels] = useState(null);

  // Copia local editable, inicializada al abrir; se descarta al cancelar.
  const planValues = useMemo(() => values ?? (paymentPlan || []), [values, paymentPlan]);
  const planLabels = useMemo(() => labels ?? (paymentPlanLabels || []), [labels, paymentPlanLabels]);

  const total = useMemo(
    () => planValues.reduce((sum, v) => sum + (Number(v) || 0), 0),
    [planValues]
  );
  const balance = (Number(contractBudget) || 0) - total;
  // Mismo semáforo del plan natural: rojo si se excede, ámbar si falta, verde en 0.
  const balanceColor = balance < 0 ? "error.main" : balance > 0 ? "warning.main" : "success.main";

  const setValueAt = (index, value) => {
    const next = [...planValues];
    next[index] = Number(value) || 0;
    setValues(next);
    if (labels === null) setLabels(planLabels);
  };

  const setLabelAt = (index, label) => {
    const next = [...planLabels];
    next[index] = label;
    setLabels(next);
    if (values === null) setValues(planValues);
  };

  const addItem = () => {
    setValues([...planValues, 0]);
    setLabels([...planLabels, `Pago ${planValues.length + 1}`]);
  };

  const removeItem = (index) => {
    setValues(planValues.filter((_, i) => i !== index));
    setLabels(planLabels.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    setValues(null);
    setLabels(null);
    onClose();
  };

  const handleSave = () => {
    onSave(planValues, planLabels);
    setValues(null);
    setLabels(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Plan de pagos (manual)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Los hitos de pago se diligencian manualmente según las condiciones acordadas en el
          contrato. El plan no es obligatorio para aprobar el contrato.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={60}>#</TableCell>
              <TableCell>Concepto</TableCell>
              <TableCell width={220}>Valor</TableCell>
              {canEdit && <TableCell width={60} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {planValues.length === 0 && (
              <TableRow>
                <TableCell colSpan={canEdit ? 4 : 3}>
                  <Typography variant="body2" color="text.secondary">
                    Sin hitos de pago diligenciados.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {planValues.map((value, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={planLabels[index] || ""}
                    InputProps={{ readOnly: !canEdit }}
                    onChange={(evt) => setLabelAt(index, evt.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <MoneyInput
                    label=""
                    value={value}
                    readOnly={!canEdit}
                    onChange={(evt) => setValueAt(index, evt.target.value)}
                  />
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <Tooltip title="Eliminar hito">
                      <IconButton size="small" onClick={() => removeItem(index)}>
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {canEdit && (
          <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={addItem}>
            Agregar hito de pago
          </Button>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 4, mt: 2 }}>
          <Typography variant="body2">
            Total plan: <strong>{currency(total, { precision: 0 }).format()}</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: balanceColor }}>
            Balance vs. presupuesto: <strong>{currency(balance, { precision: 0 }).format()}</strong>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        {canEdit && (
          <Button variant="contained" onClick={handleSave}>
            Guardar plan
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LegalPaymentPlanModal;
